import React, { useContext } from "react";
import { useNavigate } from "react-router-dom";
import { PowerIcon } from "@heroicons/react/24/outline";
import { UserContext } from "../UserContext";
import { FiArrowRight } from "react-icons/fi";
import WhiteLogo from "../../img/logo-white.webp";
import CodeNavigatorLogo from "../../img/Logo-2-100.jpg";
import ContinuumLogo from "../../img/continuum-logo.png";

// Date: 7 Aug 2026
// Ref: Continuum KB rollout (call with Manigandan, 2026-08-07).
// Shown to personal-login users once, right after authentication, so they can pick
// which knowledge base to browse. The choice is persisted via UserContext (localStorage).
export default function KbChooser() {
  const {
    darkTheme,
    userLoginInfo,
    graphData,
    setProduct,
    setResToken,
    setCodeNavigatorToken,
    setGraphData,
    setSelectedHighlight,
    setIsOpenLink,
  } = useContext(UserContext);
  const navigate = useNavigate();

  const displayName =
    userLoginInfo?.user_name || graphData?.givenName || "";

  const currentYear = new Date().getFullYear();

  const chooseProduct = (option) => {
    setProduct(option.key);
    // Land on the product's own launch URL regardless of whatever URL (e.g. a
    // slug-login path) was showing when the chooser rendered. MainWrapper
    // treats this launch path like "/" -- it's not a content slug.
    navigate(option.path, { replace: true });
  };

  // Mirrors Header.js's logoutFunc -- offered here too since this page has
  // no other way back to the login screen if the wrong account is signed in.
  const logoutFunc = () => {
    localStorage.removeItem("access_token");
    localStorage.removeItem("cn_access_token");
    localStorage.removeItem("cn_rt");
    localStorage.removeItem("uInfo");
    localStorage.removeItem("msInfo");
    localStorage.removeItem("previousUrl");
    localStorage.removeItem("currentUrl");
    localStorage.removeItem("versionNameId");
    localStorage.removeItem("cn_userInfo");
    localStorage.removeItem("product");

    setSelectedHighlight("");
    setIsOpenLink("");
    setResToken("");
    setCodeNavigatorToken("");
    setGraphData(null);
    setProduct("");

    navigate("/");
  };

  // Tailwind's JIT scanner needs full literal class names in the source --
  // interpolating a color name into a template string won't generate the CSS,
  // so each option carries its own pre-written class strings instead.
  const allOptions = [
    {
      key: "codeNavigator",
      path: "/cloudframe/codenavigator",
      logo: CodeNavigatorLogo,
      // The source asset has more blue margin above the wordmark than below,
      // so it renders low inside the full-height band -- nudge it up a touch
      // to recenter (the reveal at the bottom is the same blue, so it's seamless).
      logoClass: "-translate-y-2",
      title: "CodeNavigator",
      description:
        "Guides and reference documentation for the CodeNavigator product.",
      accentBar: "bg-cfBlue",
      linkText: "text-cfBlue",
    },
    {
      key: "continuum",
      path: "/cloudframe/continuum",
      logo: ContinuumLogo,
      title: "Continuum",
      description:
        "Migrate and unify knowledge bases into a single, always-current source of truth.",
      accentBar: "bg-gradient-to-r from-[#0c54ee] via-cfBlue to-[#062a80]",
      linkText: "text-cfBlue",
      premium: true,
    },
  ];

  // Date: 11 Aug 2026
  // Ref: This screen only renders at all when MainWrapper couldn't resolve a
  // single accessible product for the user (see getSingleAccessibleProduct),
  // which normally means both are granted. Filtering here is just a defensive
  // fallback -- e.g. access changing mid-session -- so a revoked product's
  // tile never gets offered.
  const options = allOptions.filter((option) =>
    option.key === "codeNavigator"
      ? userLoginInfo?.can_view_codenavigator
      : userLoginInfo?.can_view_continuum,
  );

  return (
    <div
      className={`fixed inset-0 flex flex-col overflow-hidden ${
        darkTheme ? "bg-[#171717] text-White" : "bg-White text-Text"
      }`}
    >
      {/* HEADER -- fixed height, never scrolls */}
      <div className="h-16 shrink-0 px-4 md:px-6 flex items-center justify-between bg-cfBlue">
        <img className="w-32 md:w-40" src={WhiteLogo} alt="Cloudframe" />
        <button
          onClick={() => logoutFunc()}
          className="flex items-center gap-1.5 rounded-full px-3 py-1.5 text-sm text-White transition-all hover:bg-white/10"
        >
          <PowerIcon className="h-4 w-4" />
          <span>Logout</span>
        </button>
      </div>

      <div className="flex flex-1 items-center justify-center overflow-y-auto px-4 py-16">
      <div className="w-full max-w-3xl">
        <div className="mb-12 flex flex-col items-center text-center">
          <span
            className={`mb-3 text-xs font-semibold uppercase tracking-[0.2em] ${
              darkTheme ? "text-gray_clr-500" : "text-gray_clr-400"
            }`}
          >
            Cloudframe Knowledge Base
          </span>
          <h1 className="text-3xl font-bold tracking-tight md:text-4xl">
            {displayName ? `Welcome, ${displayName}.` : "Welcome back."}
          </h1>
          <p
            className={`mt-3 text-sm md:text-base ${
              darkTheme ? "text-gray_clr-400" : "text-gray_clr-500"
            }`}
          >
            Choose a knowledge base to continue.
          </p>
        </div>

        {options.length === 0 ? (
          <div
            className={`rounded-2xl border p-8 text-center ${
              darkTheme
                ? "border-gray_clr-700 bg-[#1f1f1f] text-gray_clr-300"
                : "border-Border bg-White text-gray_clr-600"
            }`}
          >
            Your account doesn't have access to any documentation site.
            Please contact your administrator.
          </div>
        ) : (
        <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
          {options.map((option) => (
            <button
              key={option.key}
              onClick={() => chooseProduct(option)}
              className={`group relative flex flex-col items-start overflow-hidden rounded-2xl border text-left transition-all duration-200 ease-out focus:outline-none focus-visible:ring-2 focus-visible:ring-cfBlue focus-visible:ring-offset-2 ${
                option.premium
                  ? darkTheme
                    ? "border-gray_clr-600 bg-[#1a1a1a] shadow-[0_0_0_1px_rgba(10,77,220,0.25)] hover:-translate-y-1 hover:border-cfBlue/70 hover:shadow-[0_20px_45px_-15px_rgba(10,77,220,0.55)]"
                    : "border-cfBlue/20 bg-White shadow-[0_0_0_1px_rgba(10,77,220,0.08)] hover:-translate-y-1 hover:border-cfBlue/50 hover:shadow-[0_20px_45px_-15px_rgba(10,77,220,0.35)]"
                  : darkTheme
                    ? "border-gray_clr-700 bg-[#1f1f1f] shadow-sm hover:-translate-y-0.5 hover:border-gray_clr-500 hover:shadow-xl"
                    : "border-Border bg-White shadow-sm hover:-translate-y-0.5 hover:border-gray_clr-300 hover:shadow-2xl"
              }`}
            >
              <span
                className={`absolute inset-x-0 top-0 h-[3px] ${option.accentBar} ${
                  option.premium ? "" : "scale-x-0 group-hover:scale-x-100"
                } transition-transform duration-200 ease-out`}
              />

              <span className="flex h-16 w-full items-center justify-center overflow-hidden bg-cfBlue">
                <img
                  src={option.logo}
                  alt={option.key}
                  className={`h-full max-w-[70%] object-contain ${option.logoClass || ""}`}
                />
              </span>

              <div className="w-full p-7">
                <h3 className="text-base font-bold">{option.title}</h3>
                <p
                  className={`mt-1.5 text-sm leading-relaxed ${
                    darkTheme ? "text-gray_clr-400" : "text-gray_clr-500"
                  }`}
                >
                  {option.description}
                </p>

                <span
                  className={`mt-4 flex items-center gap-1.5 text-sm font-semibold ${option.linkText}`}
                >
                  Open
                  <FiArrowRight className="transition-transform duration-200 group-hover:translate-x-1" />
                </span>
              </div>
            </button>
          ))}
        </div>
        )}
      </div>
      </div>

      {/* FOOTER -- fixed height, never scrolls */}
      <div className="flex h-16 shrink-0 items-center bg-Black px-4 leading-3 text-White md:px-8">
        <div className="text-[13px] md:text-[15px]">
          &copy; {currentYear} CloudFrame, Inc. All rights reserved.
        </div>
      </div>
    </div>
  );
}
